import { useEffect, useMemo, useRef, useState } from "react";

const projects = [
  {
    title: "Empathetic Robot Swarm",
    categories: ["ai", "research"],
    type: { pl: "AI / badania", en: "AI / research" },
    description: {
      pl: "Symulator roju robotów ze sztuczną empatią zbudowany w Unity jako projekt badawczo-programistyczny.",
      en: "A Unity-based robot swarm simulator exploring artificial empathy and adaptive group behaviour.",
    },
    stack: ["Unity", "C#", "AI"],
    github: "https://github.com/JerKwi1/Empathetic-Robot-Simulator-in-Unity",
    featured: true,
    accent: "lime",
  },
  {
    title: "Light Paws",
    categories: ["web"],
    type: { pl: "Platforma webowa", en: "Web platform" },
    description: {
      pl: "Rozbudowana strona dla społeczności Destiny 2, łącząca wiedzę, narzędzia i nowoczesny interfejs.",
      en: "A rich Destiny 2 community website combining knowledge, tools and a modern interface.",
    },
    stack: ["React", "UX/UI", "Cloudflare"],
    live: "https://light-paws.com",
    featured: true,
    accent: "blue",
  },
  {
    title: "Lixay the Fox",
    categories: ["web", "branding"],
    type: { pl: "Portfolio kreatywne", en: "Creative portfolio" },
    description: {
      pl: "Ekspresyjne portfolio web developera i artysty 3D z autorskimi animacjami i galeriami.",
      en: "An expressive portfolio for a web developer and 3D artist, with bespoke motion and galleries.",
    },
    stack: ["React", "Motion", "Art direction"],
    live: "https://lixaythefox.com",
    featured: true,
    accent: "violet",
  },
  {
    title: "LCL Group",
    categories: ["web", "branding"],
    type: { pl: "Ekosystem marek", en: "Brand ecosystem" },
    description: {
      pl: "Serwisowa rodzina stron i współtworzone księgi znaków dla LCL Holding, Spedition i Niedoradz.",
      en: "A family of websites and co-created brand books for LCL Holding, Spedition and Niedoradz.",
    },
    stack: ["Web", "Branding", "Design system"],
    live: "https://lcl-holding.com",
    featured: true,
    accent: "orange",
  },
  {
    title: "PONACO",
    categories: ["web"],
    type: { pl: "Strona firmowa", en: "Corporate website" },
    description: {
      pl: "Lekka i czytelna strona internetowa dla firmy z Zielonej Góry, przygotowana pod ofertę B2B.",
      en: "A lightweight corporate website for a Zielona Góra company, designed around its B2B offer.",
    },
    stack: ["Web design", "Frontend", "SEO"],
    live: "https://www.ponaco.pl",
    featured: false,
    accent: "blue",
  },
  {
    title: "PRONIKO",
    categories: ["web"],
    type: { pl: "Strona firmowa", en: "Corporate website" },
    description: {
      pl: "Serwis internetowy prezentujący specjalistyczne usługi z naciskiem na przejrzystość i kontakt.",
      en: "A specialist services website focused on clarity, trust and a direct contact path.",
    },
    stack: ["Frontend", "Responsive", "SEO"],
    live: "https://www.pronikowierzytelnosci.com",
    featured: false,
    accent: "lime",
  },
  {
    title: "Harvey Scott Agency",
    categories: ["web", "branding"],
    type: { pl: "Strona agencyjna", en: "Agency website" },
    description: {
      pl: "Projekt strony dla warszawskiej agencji, wspierający komunikację marki i prezentację oferty.",
      en: "A website for a Warsaw agency supporting brand communication and service presentation.",
    },
    stack: ["Web", "Creative", "Brand"],
    live: "http://harveyscott.agency",
    featured: false,
    accent: "violet",
  },
  {
    title: "Wajnert Meble / Lovsit",
    categories: ["web", "branding"],
    type: { pl: "Projekt komercyjny", en: "Commercial project" },
    description: {
      pl: "Realizacja internetowa dla producenta mebli wypoczynkowych, łącząca produkt z wizerunkiem marki.",
      en: "A web project for a furniture manufacturer connecting product presentation with brand image.",
    },
    stack: ["Web", "Content", "Branding"],
    live: "http://lovsit.pl",
    featured: false,
    accent: "orange",
  },
  {
    title: "Briju / Big Star / Herbapol",
    categories: ["branding"],
    type: { pl: "Współprace projektowe", en: "Project collaborations" },
    description: {
      pl: "Wybrane realizacje komercyjne i prace projektowe dla rozpoznawalnych polskich marek.",
      en: "Selected commercial work and project collaborations for established Polish brands.",
    },
    stack: ["Branding", "Web", "Production"],
    featured: false,
    accent: "blue",
  },
];

const projectFilters = [
  { id: "all", label: { pl: "Wszystkie", en: "All" } },
  { id: "web", label: { pl: "Web", en: "Web" } },
  { id: "ai", label: { pl: "AI", en: "AI" } },
  { id: "branding", label: { pl: "Branding", en: "Branding" } },
  { id: "research", label: { pl: "Badania", en: "Research" } },
];

const codeGlyphs = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&<>/{}[]";

function encodeText(value, progress, reveal) {
  const characters = Array.from(value);
  return characters
    .map((character, index) => {
      if (!/[\p{L}\p{N}]/u.test(character)) return character;
      const threshold = index / Math.max(characters.length - 1, 1);
      const showOriginal = reveal ? threshold <= progress : threshold < 1 - progress;
      return showOriginal ? character : codeGlyphs[Math.floor(Math.random() * codeGlyphs.length)];
    })
    .join("");
}

function getVisibleTextNodes() {
  const nodes = [];
  const roots = [document.querySelector(".site-nav"), document.querySelector("main")].filter(Boolean);

  roots.forEach((root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!node.nodeValue.trim() || !parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('[data-no-scramble], [aria-hidden="true"], .sr-only')) {
          return NodeFilter.FILTER_REJECT;
        }

        const rect = parent.getBoundingClientRect();
        const style = window.getComputedStyle(parent);
        const isVisible =
          rect.bottom >= 0 &&
          rect.top <= window.innerHeight &&
          rect.right >= 0 &&
          rect.left <= window.innerWidth &&
          style.display !== "none" &&
          style.visibility !== "hidden";

        return isVisible ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }
  });

  return nodes;
}

function lockTextLayouts(nodes) {
  const elements = [...new Set(nodes.map((node) => node.parentElement).filter(Boolean))];
  const lockedElements = [];

  elements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    element.style.setProperty("--language-lock-width", rect.width + "px");
    element.style.setProperty("--language-lock-height", rect.height + "px");
    element.classList.add("language-text-lock");
    lockedElements.push(element);
  });

  return () => {
    lockedElements.forEach((element) => {
      element.classList.remove("language-text-lock");
      element.style.removeProperty("--language-lock-width");
      element.style.removeProperty("--language-lock-height");
    });
  };
}

const experience = [
  {
    date: { pl: "11.2024 — obecnie", en: "11.2024 — present" },
    role: { pl: "Młodszy administrator", en: "Junior Administrator" },
    place: "FORCOM / Poznań",
    text: {
      pl: "Monitoring i zarządzanie systemami IT, wsparcie użytkowników, konfiguracja sprzętu i udział we wdrażaniu nowych rozwiązań.",
      en: "IT systems monitoring and management, user support, hardware configuration and support for new technology deployments.",
    },
  },
  {
    date: { pl: "01.2020 — obecnie", en: "01.2020 — present" },
    role: { pl: "Freelance Web Creator", en: "Freelance Web Creator" },
    place: "Independent / Poznań",
    text: {
      pl: "Projektowanie i realizacja stron, systemów identyfikacji oraz materiałów cyfrowych dla firm, marek i społeczności.",
      en: "Design and delivery of websites, identity systems and digital assets for companies, brands and communities.",
    },
  },
  {
    date: { pl: "07.2020 — 09.2020", en: "07.2020 — 09.2020" },
    role: { pl: "Edytor filmów", en: "Video Editor" },
    place: { pl: "Studio Kreacja / Śremski Ośrodek Kultury", en: "Studio Kreacja / Śrem Cultural Centre" },
    text: {
      pl: "Montaż materiałów dla marki MrPost oraz długofalowa współpraca przy produkcji treści wideo.",
      en: "Video editing for the MrPost brand and long-term collaboration on digital video production.",
    },
  },
];

const copy = {
  pl: {
    nav: ["O mnie", "Kompetencje", "Projekty", "Kariera", "Kontakt"],
    status: "Dostępny dla wybranych projektów",
    role: "Cybersecurity · Artificial Intelligence · Web",
    heroTop: "MGR INŻ. / POZNAŃ / PL",
    heroTitleA: "BEZPIECZNE",
    heroTitleB: "CYFROWE",
    heroTitleC: "SYSTEMY",
    heroIntro:
      "Projektuję, rozwijam i zabezpieczam rozwiązania cyfrowe — od infrastruktury IT, przez eksperymenty z AI, po dopracowane produkty webowe.",
    seeWork: "Zobacz realizacje",
    download: "Pobierz CV",
    scroll: "Przewiń, by poznać mój profil",
    aboutLabel: "01 / O mnie",
    aboutTitle: "Technologia ma działać, wyglądać i budzić zaufanie",
    aboutBody:
      "Łączę praktyczne doświadczenie administracyjne i webowe z zapleczem naukowym w obszarze cyberbezpieczeństwa oraz sztucznej inteligencji. Najlepiej czuję się tam, gdzie analityczne myślenie spotyka się z dobrym projektem.",
    aboutQuote: "Buduję rozwiązania, które są użyteczne dzisiaj i gotowe na to, co będzie jutro",
    based: "Poznań / praca zdalna",
    years: "lat w projektach webowych",
    degree: "mgr inż. Cybersecurity + AI",
    expertiseLabel: "02 / Kompetencje",
    expertiseTitle: "Trzy perspektywy — jeden spójny proces",
    expertiseIntro: "Od diagnozy i architektury po interfejs, wdrożenie i dalszy rozwój.",
    expertise: [
      {
        id: "01",
        title: "Cyberbezpieczeństwo",
        text: "Monitoring systemów, administracja, dokumentacja, analiza ryzyka i świadome projektowanie bezpiecznych rozwiązań.",
        tags: ["Systemy IT", "Hardening", "Procedury", "Linux / Windows"],
      },
      {
        id: "02",
        title: "AI i badania",
        text: "Uczenie maszynowe, modelowanie zachowań, eksperymenty symulacyjne i przekładanie hipotez na działający kod.",
        tags: ["Python", "Unity / C#", "Machine learning", "Research"],
      },
      {
        id: "03",
        title: "Web development",
        text: "Responsywne strony i doświadczenia internetowe z mocnym kierunkiem wizualnym, wydajnością i dostępnością.",
        tags: ["React", "JavaScript", "UX/UI", "Cloudflare"],
      },
    ],
    workLabel: "03 / Wybrane realizacje",
    workTitle: "Projekty, które łączą kod z konkretnym celem",
    workIntro: "Badania, strony dla biznesu, społeczności i marek — różne skale, ten sam nacisk na jakość.",
    filterLabel: "Filtruj realizacje",
    filterHint: "Wybierz obszar",
    filterResult: "Widoczne realizacje: ",
    viewProject: "Otwórz projekt",
    viewCode: "Zobacz kod",
    showAll: "Pokaż wszystkie realizacje",
    showLess: "Pokaż wybrane",
    careerLabel: "04 / Droga zawodowa",
    careerTitle: "Doświadczenie budowane na styku wielu dyscyplin",
    education: "Wykształcenie",
    eduItems: [
      ["2024 — 2025", "Uniwersytet im. Adama Mickiewicza", "Magister — Cyberbezpieczeństwo i sztuczna inteligencja"],
      ["2020 — 2024", "Uniwersytet im. Adama Mickiewicza", "Inżynier — Wydział Matematyki i Informatyki"],
      ["2017 — 2020", "Liceum im. Józefa Wybickiego", "Klasa matematyczno-fizyczna dwujęzyczna"],
    ],
    researchLabel: "05 / Nauka i osiągnięcia",
    researchTitle: "Nie tylko wdrażam — sprawdzam, dlaczego to działa",
    researchBody:
      "Moje badania dotyczą empatycznych rojów robotów, ich adaptacji i wpływu początkowej więzi na zachowanie grupy. Wyniki prezentowałem w środowisku naukowym i rozwijałem w autorskim symulatorze.",
    achievements: [
      ["01", "Publikacja", "Simulation of Empathetic Robots Interaction in Swarm"],
      ["02", "Konferencja", "Udział i prowadzenie panelu podczas PP-RAI 2025"],
      ["03", "Projekt badawczy", "Symulator roju robotów ze sztuczną empatią w Unity"],
      ["04", "Język", "Angielski na poziomie C1"],
    ],
    contactLabel: "06 / Kontakt",
    contactTitleA: "MASZ POMYSŁ?",
    contactTitleB: "POROZMAWIAJMY",
    contactText: "Opisz krótko projekt, termin i oczekiwany zakres. Odpowiem z konkretną propozycją kolejnych kroków.",
    write: "Napisz wiadomość",
    call: "Zadzwoń",
    location: "Poznań, Polska / zdalnie",
    back: "Do góry",
    footer: "Projekt i wykonanie — Jerzy Kwiatkowski",
    langLabel: "Switch to English",
    menuOpen: "Otwórz menu",
    menuClose: "Zamknij menu",
    intro: "PORTFOLIO / 2026",
  },
  en: {
    nav: ["About", "Expertise", "Projects", "Career", "Contact"],
    status: "Available for selected projects",
    role: "Cybersecurity · Artificial Intelligence · Web",
    heroTop: "MSc ENG. / POZNAŃ / PL",
    heroTitleA: "SECURE",
    heroTitleB: "DIGITAL",
    heroTitleC: "SYSTEMS",
    heroIntro:
      "I design, develop and secure digital solutions — from IT infrastructure and AI experiments to polished web products.",
    seeWork: "View selected work",
    download: "Download CV",
    scroll: "Scroll to explore my profile",
    aboutLabel: "01 / About",
    aboutTitle: "Technology should work, look right and inspire trust",
    aboutBody:
      "I combine hands-on systems and web experience with an academic background in cybersecurity and artificial intelligence. I do my best work where analytical thinking meets thoughtful design.",
    aboutQuote: "I build solutions that are useful today and ready for what comes next",
    based: "Poznań / remote",
    years: "years in web projects",
    degree: "MSc Eng. Cybersecurity + AI",
    expertiseLabel: "02 / Expertise",
    expertiseTitle: "Three perspectives — one coherent process",
    expertiseIntro: "From diagnosis and architecture to interface, deployment and continuous improvement.",
    expertise: [
      {
        id: "01",
        title: "Cybersecurity",
        text: "Systems monitoring, administration, documentation, risk analysis and security-minded solution design.",
        tags: ["IT systems", "Hardening", "Procedures", "Linux / Windows"],
      },
      {
        id: "02",
        title: "AI & research",
        text: "Machine learning, behavioural modelling, simulation experiments and turning hypotheses into working code.",
        tags: ["Python", "Unity / C#", "Machine learning", "Research"],
      },
      {
        id: "03",
        title: "Web development",
        text: "Responsive websites and digital experiences with strong visual direction, performance and accessibility.",
        tags: ["React", "JavaScript", "UX/UI", "Cloudflare"],
      },
    ],
    workLabel: "03 / Selected work",
    workTitle: "Projects where code meets a concrete goal",
    workIntro: "Research, business websites, communities and brands — different scales, the same focus on quality.",
    filterLabel: "Filter projects",
    filterHint: "Choose a field",
    filterResult: "Visible projects: ",
    viewProject: "Open project",
    viewCode: "View code",
    showAll: "Show all projects",
    showLess: "Show selected",
    careerLabel: "04 / Career path",
    careerTitle: "Experience shaped at the intersection of disciplines",
    education: "Education",
    eduItems: [
      ["2024 — 2025", "Adam Mickiewicz University", "MSc — Cybersecurity and Artificial Intelligence"],
      ["2020 — 2024", "Adam Mickiewicz University", "BEng — Faculty of Mathematics and Computer Science"],
      ["2017 — 2020", "Józef Wybicki High School", "Bilingual mathematics and physics programme"],
    ],
    researchLabel: "05 / Research & achievements",
    researchTitle: "I do not only deploy — I ask why it works",
    researchBody:
      "My research explores empathetic robot swarms, their adaptation and the effect of initial bonding on group behaviour. I presented the results in academia and developed them through an original simulator.",
    achievements: [
      ["01", "Publication", "Simulation of Empathetic Robots Interaction in Swarm"],
      ["02", "Conference", "Participant and panel lead at PP-RAI 2025"],
      ["03", "Research project", "Unity simulator of a robot swarm with artificial empathy"],
      ["04", "Language", "English proficiency at C1 level"],
    ],
    contactLabel: "06 / Contact",
    contactTitleA: "HAVE AN IDEA?",
    contactTitleB: "LET'S TALK",
    contactText: "Share a short outline, timeline and expected scope. I will reply with a concrete proposal for the next steps.",
    write: "Send an email",
    call: "Call me",
    location: "Poznań, Poland / remote",
    back: "Back to top",
    footer: "Designed and built by Jerzy Kwiatkowski",
    langLabel: "Przełącz na polski",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    intro: "PORTFOLIO / 2026",
  },
};

function SectionTitle({ label, title, intro, dark = false }) {
  return (
    <div className={"section-heading reveal" + (dark ? " section-heading--dark" : "")}>
      <p className="section-label">{label}</p>
      <div>
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </div>
    </div>
  );
}

function App() {
  const [lang, setLang] = useState("pl");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [displayedFilter, setDisplayedFilter] = useState("all");
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const filterTimer = useRef(null);
  const languageFrame = useRef(null);
  const languageLayoutUnlock = useRef(null);
  const pendingLanguageReveal = useRef(false);
  const t = copy[lang];

  const visibleProjects = useMemo(
    () =>
      displayedFilter === "all"
        ? projects
        : projects.filter((project) => project.categories.includes(displayedFilter)),
    [displayedFilter],
  );

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title =
      lang === "pl"
        ? "Jerzy Kwiatkowski — Cybersecurity, AI & Web"
        : "Jerzy Kwiatkowski — Cybersecurity, AI & Web";
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.content =
        lang === "pl"
          ? "Portfolio Jerzego Kwiatkowskiego — cyberbezpieczeństwo, sztuczna inteligencja i nowoczesne rozwiązania webowe."
          : "Portfolio of Jerzy Kwiatkowski — cybersecurity, artificial intelligence and modern web solutions.";
    }
  }, [lang]);

  useEffect(() => {
    if (!pendingLanguageReveal.current) return;
    pendingLanguageReveal.current = false;

    const nodes = getVisibleTextNodes();
    const entries = nodes.map((node) => ({ node, target: node.nodeValue }));
    if (languageLayoutUnlock.current) languageLayoutUnlock.current();
    languageLayoutUnlock.current = lockTextLayouts(nodes);
    entries.forEach(({ node, target }) => {
      node.nodeValue = encodeText(target, 0, true);
    });

    const startedAt = performance.now();
    const revealDuration = 520;
    const stagger = 18;
    const maximumStagger = Math.min(entries.length - 1, 9) * stagger;

    const revealFrame = (now) => {
      const elapsed = now - startedAt;

      entries.forEach(({ node, target }, index) => {
        const delay = (index % 10) * stagger;
        const localProgress = Math.min(1, Math.max(0, (elapsed - delay) / revealDuration));
        const easedProgress = 1 - Math.pow(1 - localProgress, 3);
        node.nodeValue = encodeText(target, easedProgress, true);
      });

      if (elapsed < revealDuration + maximumStagger) {
        languageFrame.current = window.requestAnimationFrame(revealFrame);
        return;
      }

      entries.forEach(({ node, target }) => {
        node.nodeValue = target;
      });
      if (languageLayoutUnlock.current) languageLayoutUnlock.current();
      languageLayoutUnlock.current = null;
      document.body.classList.remove("language-is-switching");
      setIsLanguageSwitching(false);
      languageFrame.current = null;
    };

    languageFrame.current = window.requestAnimationFrame(revealFrame);
  }, [lang]);

  useEffect(() => {
    const introTimer = window.setTimeout(() => setIntroDone(true), 1100);
    const onScroll = () => {
      const maximum = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(maximum > 0 ? window.scrollY / maximum : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.clearTimeout(introTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    const elements = document.querySelectorAll(".reveal");
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [lang, displayedFilter]);

  useEffect(() => {
    document.body.classList.toggle("menu-is-open", menuOpen);
    return () => document.body.classList.remove("menu-is-open");
  }, [menuOpen]);

  useEffect(
    () => () => {
      if (filterTimer.current) window.clearTimeout(filterTimer.current);
      if (languageFrame.current) window.cancelAnimationFrame(languageFrame.current);
      if (languageLayoutUnlock.current) languageLayoutUnlock.current();
      document.body.classList.remove("language-is-switching");
    },
    [],
  );

  const closeMenu = () => setMenuOpen(false);

  const switchLanguage = () => {
    if (isLanguageSwitching) return;

    const nextLanguage = lang === "pl" ? "en" : "pl";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setLang(nextLanguage);
      return;
    }

    const nodes = getVisibleTextNodes();
    const entries = nodes.map((node) => ({ node, source: node.nodeValue }));
    if (languageLayoutUnlock.current) languageLayoutUnlock.current();
    languageLayoutUnlock.current = lockTextLayouts(nodes);
    const startedAt = performance.now();
    const encodeDuration = 230;

    setIsLanguageSwitching(true);
    document.body.classList.add("language-is-switching");

    const encodeFrame = (now) => {
      const progress = Math.min(1, (now - startedAt) / encodeDuration);
      const easedProgress = progress * progress;

      entries.forEach(({ node, source }) => {
        node.nodeValue = encodeText(source, easedProgress, false);
      });

      if (progress < 1) {
        languageFrame.current = window.requestAnimationFrame(encodeFrame);
        return;
      }

      entries.forEach(({ node, source }) => {
        node.nodeValue = source;
      });
      if (languageLayoutUnlock.current) languageLayoutUnlock.current();
      languageLayoutUnlock.current = null;
      pendingLanguageReveal.current = true;
      languageFrame.current = null;
      setLang(nextLanguage);
    };

    languageFrame.current = window.requestAnimationFrame(encodeFrame);
  };

  const selectProjectFilter = (filterId) => {
    if (filterId === activeFilter) return;

    setActiveFilter(filterId);
    setIsFiltering(true);
    if (filterTimer.current) window.clearTimeout(filterTimer.current);

    const transitionDelay = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : Math.min(440, 280 + visibleProjects.length * 20);

    filterTimer.current = window.setTimeout(() => {
      setDisplayedFilter(filterId);
      setIsFiltering(false);
    }, transitionDelay);
  };

  const trackPointer = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--pointer-x", event.clientX - rect.left + "px");
    event.currentTarget.style.setProperty("--pointer-y", event.clientY - rect.top + "px");
  };

  return (
    <>
      <a className="skip-link" href="#content">
        {lang === "pl" ? "Przejdź do treści" : "Skip to content"}
      </a>

      <div className={"intro-screen" + (introDone ? " intro-screen--done" : "")} aria-hidden="true">
        <div className="intro-mark">
          <span>JK</span>
          <i />
          <small>{t.intro}</small>
        </div>
      </div>

      <div className="scroll-progress" style={{ transform: "scaleX(" + progress + ")" }} />

      <header className="site-nav">
        <a className="brand" href="#top" onClick={closeMenu} aria-label="Jerzy Kwiatkowski — top">
          JK<span>®</span>
        </a>

        <div className="availability">
          <i />
          <span>{t.status}</span>
        </div>

        <nav className={"nav-links" + (menuOpen ? " nav-links--open" : "")} aria-label="Main navigation">
          {["about", "expertise", "work", "career", "contact"].map((id, index) => (
            <a href={"#" + id} onClick={closeMenu} key={id}>
              {t.nav[index]}
            </a>
          ))}
        </nav>

        <div className="nav-actions">
          <button
            className="language"
            type="button"
            onClick={switchLanguage}
            aria-label={t.langLabel}
            aria-busy={isLanguageSwitching}
            data-no-scramble
            disabled={isLanguageSwitching}
          >
            {lang === "pl" ? "EN" : "PL"}
          </button>
          <button
            className={"menu-button" + (menuOpen ? " menu-button--open" : "")}
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t.menuClose : t.menuOpen}
          >
            <span />
            <span />
          </button>
        </div>
      </header>

      <main id="content">
        <section id="top" className="hero" onPointerMove={trackPointer}>
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-signal" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>

          <div className="hero-meta reveal">
            <p>{t.heroTop}</p>
            <p>{t.role}</p>
          </div>

          <div className="hero-title reveal">
            <p>JERZY KWIATKOWSKI</p>
            <h1>
              <span>{t.heroTitleA}</span>
              <span className="hero-title-outline">{t.heroTitleB}</span>
              <span>{t.heroTitleC}</span>
            </h1>
          </div>

          <div className="hero-bottom">
            <div className="hero-copy reveal">
              <p>{t.heroIntro}</p>
              <div className="hero-buttons">
                <a className="button button--solid" href="#work">
                  {t.seeWork} <span>↘</span>
                </a>
                <a className="button button--line" href="/Jerzy_Kwiatkowski_CV.pdf" download>
                  {t.download} <span>↓</span>
                </a>
              </div>
            </div>

            <div className="hero-monogram reveal" aria-hidden="true">
              <span className="monogram-index">JK / 01</span>
              <strong>J</strong>
              <strong>K</strong>
              <i className="crosshair">+</i>
              <small>52.4064° N<br />16.9252° E</small>
            </div>
          </div>

          <a className="hero-scroll" href="#about">
            <span>{t.scroll}</span>
            <i />
          </a>
        </section>

        <section id="about" className="about section-shell">
          <SectionTitle label={t.aboutLabel} title={t.aboutTitle} />
          <div className="about-layout">
            <div className="about-copy reveal">
              <p>{t.aboutBody}</p>
              <blockquote>{t.aboutQuote}</blockquote>
            </div>
            <dl className="about-facts reveal">
              <div>
                <dt>01</dt>
                <dd>{t.based}</dd>
              </div>
              <div>
                <dt>06+</dt>
                <dd>{t.years}</dd>
              </div>
              <div>
                <dt>MSc</dt>
                <dd>{t.degree}</dd>
              </div>
            </dl>
          </div>
        </section>

        <section id="expertise" className="expertise">
          <div className="section-shell">
            <SectionTitle
              label={t.expertiseLabel}
              title={t.expertiseTitle}
              intro={t.expertiseIntro}
              dark
            />
            <div className="expertise-list">
              {t.expertise.map((item) => (
                <article className="expertise-row reveal" key={item.id}>
                  <span className="expertise-id">{item.id}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <ul>
                    {item.tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                  <span className="expertise-arrow" aria-hidden="true">↘</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="work section-shell">
          <SectionTitle label={t.workLabel} title={t.workTitle} intro={t.workIntro} />
          <div className="project-filterbar reveal">
            <p>{t.filterHint}</p>
            <div className="project-filters" role="group" aria-label={t.filterLabel}>
              {projectFilters.map((filter) => {
                const count =
                  filter.id === "all"
                    ? projects.length
                    : projects.filter((project) => project.categories.includes(filter.id)).length;

                return (
                  <button
                    className={"project-filter" + (activeFilter === filter.id ? " is-active" : "")}
                    type="button"
                    aria-pressed={activeFilter === filter.id}
                    onClick={() => selectProjectFilter(filter.id)}
                    key={filter.id}
                  >
                    <span>{filter.label[lang]}</span>
                    <sup>{String(count).padStart(2, "0")}</sup>
                    <i aria-hidden="true" />
                  </button>
                );
              })}
            </div>
            <p className="sr-only" role="status" aria-live="polite">
              {t.filterResult}{visibleProjects.length}
            </p>
          </div>
          <div
            className={"project-grid" + (isFiltering ? " project-grid--filtering" : "")}
            aria-busy={isFiltering}
          >
            {visibleProjects.map((project, index) => (
              <article
                className={"project-card project-card--" + project.accent + " reveal"}
                key={displayedFilter + "-" + project.title}
                style={{ "--filter-index": index }}
              >
                <div className="project-visual">
                  <span className="project-number">{String(index + 1).padStart(2, "0")}</span>
                  <span className="project-mark">{project.title.slice(0, 2).toUpperCase()}</span>
                  <div className="project-lines" aria-hidden="true" />
                  <p>{project.type[lang]}</p>
                </div>
                <div className="project-info">
                  <div>
                    <p>{project.type[lang]}</p>
                    <h3>{project.title}</h3>
                  </div>
                  <p>{project.description[lang]}</p>
                  <ul>
                    {project.stack.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <div className="project-links">
                    {project.live && (
                      <a href={project.live} target="_blank" rel="noreferrer">
                        {t.viewProject} <span>↗</span>
                      </a>
                    )}
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noreferrer">
                        {t.viewCode} <span>↗</span>
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="career" className="career">
          <div className="section-shell">
            <SectionTitle label={t.careerLabel} title={t.careerTitle} dark />
            <div className="career-layout">
              <div className="timeline">
                {experience.map((item, index) => (
                  <article className="timeline-item reveal" key={item.role.pl}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <p className="timeline-date">{item.date[lang]}</p>
                    <div>
                      <h3>{item.role[lang]}</h3>
                      <p className="timeline-place">
                        {typeof item.place === "string" ? item.place : item.place[lang]}
                      </p>
                      <p>{item.text[lang]}</p>
                    </div>
                  </article>
                ))}
              </div>
              <aside className="education reveal">
                <p className="education-title">{t.education}</p>
                {t.eduItems.map((item) => (
                  <div key={item[0]}>
                    <span>{item[0]}</span>
                    <h3>{item[1]}</h3>
                    <p>{item[2]}</p>
                  </div>
                ))}
              </aside>
            </div>
          </div>
        </section>

        <section className="research section-shell">
          <SectionTitle label={t.researchLabel} title={t.researchTitle} />
          <div className="research-layout">
            <p className="research-intro reveal">{t.researchBody}</p>
            <div className="achievement-list">
              {t.achievements.map((item) => (
                <article className="achievement reveal" key={item[0]}>
                  <span>{item[0]}</span>
                  <p>{item[1]}</p>
                  <h3>{item[2]}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="contact">
          <div className="contact-grid" aria-hidden="true" />
          <div className="section-shell">
            <p className="section-label section-label--light reveal">{t.contactLabel}</p>
            <div className="contact-title reveal">
              <h2>
                <span>{t.contactTitleA}</span>
                <span>{t.contactTitleB}</span>
              </h2>
            </div>
            <div className="contact-bottom reveal">
              <p>{t.contactText}</p>
              <div className="contact-actions">
                <a href="mailto:jerzy@jerzykwiatkowski.pl">
                  <small>{t.write}</small>
                  <strong>jerzy@jerzykwiatkowski.pl</strong>
                  <span>↗</span>
                </a>
                <a href="tel:+48698898583">
                  <small>{t.call}</small>
                  <strong>+48 698 898 583</strong>
                  <span>↗</span>
                </a>
              </div>
            </div>
            <footer>
              <p>{t.footer} · © 2026</p>
              <p>{t.location}</p>
              <a href="#top">{t.back} ↑</a>
            </footer>
          </div>
        </section>
      </main>
    </>
  );
}

export default App;